import { makeRequest, thunkify } from '../../services/http';
import {
    ICustomerResetPasswordResponse,
    ICustomerVerificationCodeResponse,
    ICustomerVerifyCodeResponse,
    ICustomersGetResponse,
    ICustomersInsuranceManualResponse,
    ICustomersInsuranceUploadResponse,
    ICustomersLoginResponse,
    ICustomersUserOnboardingResponse,
    ICustomersMonographResponse,
    ICustomersValidateResponse,
    ICustomersTestInsuranceResponse
} from './models/response';
import {
    ICustomersCreateInsuranceRequest,
    ICustomersDeleteInsuranceRequest,
    ICustomersLoginRequest,
    ICustomersMetadataRequest,
    ICustomersVerificationCodeRequest,
    ICustomersVerificationNewPassRequest,
    ICustomersRegisterRequest,
    ICustomerVerifyCodeRequest,
    ICustomersVerificationCodeVerifyingRequest,
    AuthCardCreateRequest,
    ICustomerDeletePaymentMethodRequest,
    ICustomerUpdatePreferredPaymentMethodRequest,
    ICustomerTestInsuranceRequestModel,
    ICustomersValidateRequest,
    ICustomersGenerateIdentityCodeRequest,
    ICustomersVerifyIdentityCodeRequest,
    ICustomersSetPasswordRequest,
    ICustomersCodeByAuthRequest,
    ICustomersVerifyByAuthRequest
} from './models/request';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import { IDeliveryTicket, IInvoice, IPendingPrescription, ILoginErrorResponse } from '@/shared/types/interfaces';
import { ICustomerRequestItem, ICustomerRequestItems, ICustomerRequestPayload } from '@/shared/types/interfaces/customer-requests';
import { toast } from 'react-toastify';
import _ from 'lodash';

const controllerName = 'customers';
export const queryParamsForPAT = [
    'ERX_REFILL_REQUEST_PENDING',
    'FAX_REFILL_REQUEST_PENDING',
    'ERX_NEWRX_REQUEST_PENDING',
    'FAX_NEWRX_REQUEST_PENDING',
    'FAX_TRANSFER_REQUEST_PENDING'
];

export class CustomersController {
    static get = thunkify<ICustomersGetResponse>('get', controllerName, '', async (request, data) => request(data));

    static login = thunkify<ICustomerVerifyCodeResponse, ICustomersLoginRequest, ILoginErrorResponse>(
        'post',
        controllerName,
        'login',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json'
                }
            })
    );

    static register = thunkify<ICustomerVerifyCodeResponse, ICustomersRegisterRequest>(
        'post',
        controllerName,
        'register',
        async (request, data) =>
            request(data, {
                formUrlencoded: false,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json'
                }
            })
    );

    static confirmCode = thunkify<ICustomerVerifyCodeResponse, ICustomerVerifyCodeRequest>(
        'post',
        controllerName,
        'verify?noErrorLog',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json'
                }
            })
    );

    static logout = thunkify<ICustomersLoginResponse>('post', controllerName, 'logout');

    static generateVerificationCode = thunkify<ICustomerVerificationCodeResponse, ICustomersVerificationCodeRequest>(
        'post',
        controllerName,
        'generate/code',
        async (request, data) => {
            return request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
        }
             
    );

    static generateIdentityCode = thunkify<ICustomerVerificationCodeResponse, ICustomersGenerateIdentityCodeRequest>(
        'post',
        controllerName,
        'generate/code-by-identity',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
    );

    static codeByAuth = thunkify<ICustomerVerificationCodeResponse, ICustomersCodeByAuthRequest>(
        'post',
        controllerName,
        'generate/code-by-auth',
        async (request, data) => {
            const { authToken, ...rest } = data;
            return request(rest, {
                formUrlencoded: true,
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
        }
    );

    static verifyByAuth = thunkify<ICustomerVerificationCodeResponse, ICustomersVerifyByAuthRequest>(
        'post',
        controllerName,
        'verify-by-auth',
        async (request, data) => {
            const { authToken, ...rest } = data;
            return request(rest, {
                formUrlencoded: true,
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
        }
    );

    static verifyIdentityCode = thunkify<ICustomersLoginResponse, ICustomersVerifyIdentityCodeRequest>(
        'post',
        controllerName,
        'verify-by-identity',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
    );

    static setPassword = thunkify<null, ICustomersSetPasswordRequest>('patch', controllerName, 'set-password', async (request, data) =>
        request(_.omit(data, 'token'), {
            formUrlencoded: true,
            headers: {
                Authorization: `Bearer ${data.token}`,
                Accept: 'application/com.quickfill.customerapp-v3.1+json',
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        })
    );

    static validate = thunkify<ICustomersValidateResponse, Partial<ICustomersValidateRequest>>(
        'post',
        controllerName,
        'validate',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json',
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
    );

    static verifyVerificationCode = thunkify<ICustomerVerifyCodeResponse, ICustomersVerificationCodeVerifyingRequest>(
        'post',
        controllerName,
        'verify',
        async (request, data) => request(data, { formUrlencoded: true })
    );

    static confirmNewPassword = thunkify<ICustomerResetPasswordResponse, ICustomersVerificationNewPassRequest>(
        'post',
        controllerName,
        'password/reset',
        async (request, data) =>
            request(data, {
                formUrlencoded: true,
                headers: {
                    Accept: 'application/com.quickfill.customerapp-v3.1+json'
                }
            })
    );

    static eligibility = thunkify<ICustomersInsuranceManualResponse[]>('post', controllerName, 'eligibility');

    static insuranceCreate = thunkify<ICustomersInsuranceManualResponse, ICustomersCreateInsuranceRequest>(
        'post',
        controllerName,
        'insurances',
        undefined
    );

    static insuranceUpload = thunkify<ICustomersInsuranceUploadResponse, FormData>('post', controllerName, 'insurance-upload');

    static signatureUpload = thunkify<ICustomersInsuranceUploadResponse, FormData>('post', controllerName, 'upload-documents?documentType=TRANSFER_AUTH_CUSTOMER_SIGNATURE');

    static testInsurance = thunkify<ICustomersTestInsuranceResponse, ICustomerTestInsuranceRequestModel>(
        'post',
        controllerName,
        'insurances/:id/test-insurance',
        (request, data) => {
            return request(data, { urlParams: { id: data.id } });
        });

    static insuranceDelete = thunkify<null, ICustomersDeleteInsuranceRequest>(
        'delete',
        controllerName,
        'insurances/:id',
        (request, data) => {
            return request({
                urlParams: { id: data.id },
                params: { version: data.version }
            });
        }
    );

    static addPaymentMethod = thunkify<null, AuthCardCreateRequest>('post', controllerName, 'credit-cards');

    static paymentMethodDelete = thunkify<null, ICustomerDeletePaymentMethodRequest>(
        'delete',
        controllerName,
        'credit-cards/:id',
        (request, data) => {
            return request({
                urlParams: { id: data.id },
                params: { version: data.ver }
            });
        }
    );

    static paymentMethodUpdatePreferred = thunkify<null, ICustomerUpdatePreferredPaymentMethodRequest>(
        'patch',
        controllerName,
        'credit-cards/:id/preferred',
        (request, data) => {
            return request(data, {
                urlParams: { id: data.id }
            });
        }
    );

    static insuranceUploadDelete = thunkify<null, ICustomersDeleteInsuranceRequest>(
        'delete',
        controllerName,
        'insurance-uploads/:id',
        (request, data) => {
            return request({
                urlParams: { id: data.id },
                params: { version: data.version }
            });
        }
    );

    static addTransferPrescription = thunkify<ITransferPrescriptions | null, ITransferPrescriptions>(
        'post',
        controllerName,
        'transfer-prescriptions',
        async (request, data) => {
            let res: ITransferPrescriptions | null;
            try {
                res = await request(data);
                return res;
            } catch (error) {
                toast.error('Failed to add medication');
                return null;
            }
        }
    );

    static getMonograph = (ndc: string) => {
        return makeRequest<ICustomersMonographResponse[]>('get', controllerName, 'monograph/:ndc/en', { urlParams: { ndc } });
    };

    static removeTransferPrescription = thunkify<number | string, ITransferPrescriptions>(
        'delete',
        controllerName,
        'transfer-prescriptions/:id',
        async (request, data) => {
            await request({
                urlParams: { id: data.id },
                params: { version: data.ver }
            });

            return data.id;
        }
    );

    static updateMetadata = thunkify<
        ICustomersUserOnboardingResponse,
        ICustomersMetadataRequest<ICustomersUserOnboardingResponse['details']>
    >('put', controllerName, 'onboarding/:id', (request, data) => {
        return request(data, {
            urlParams: { id: data.id }
        });
    });

    static onFinishOnboarding = thunkify<unknown, void>('post', controllerName, 'complete-onboarding', async (request, data) =>
        request(data, { formUrlencoded: false })
    );

    static getPrescriptions = thunkify<ITransferPrescriptions[]>(
        'get',
        controllerName,
        'prescriptions?active=false', // we pass active false to get all the prescriptions
        async request => await request()
    );

    static getPendingPrescriptions = thunkify<IPendingPrescription[]>(
        'get',
        controllerName,
        `prescription-action-trackings?${queryParamsForPAT.map(param => `type=${param}`).join('&')}`,
        async request => await request()
    );

    static getInvoices = thunkify<IInvoice[], string | undefined>(
        'get',
        controllerName,
        'invoices',
        async (request, data) => await request(data)
    );

    static getDeliveryTickets = thunkify<IDeliveryTicket[]>('get', controllerName, 'delivery-tickets', async request => await request());
    static getCustomerRequests = thunkify<ICustomerRequestItems>('get', controllerName, 'requests', async request => await request());
    static addCustomerRequest = thunkify<ICustomerRequestItem, ICustomerRequestPayload>(
        'post',
        controllerName,
        'requests',
        async (request, data) => await request(data)
    );
}

export * from './models/request';
export * from './models/response';
export * from './models/enums';
